/**
 * Log Payment DTO - Chuẩn hóa dữ liệu import log nạp tiền
 * Đồng bộ với backend Laravel/NestJS
 */

import { PaymentMethodEnum, PaymentMethodLabel, PaymentMethodCodeByLabel } from '@/core/enums/logPayment.enum';

/**
 * Input DTO - Chuẩn hóa dữ liệu gửi lên API import
 * @param {Array} csvData - Dữ liệu từ CSV
 * @returns {Array} Dữ liệu đã chuẩn hóa cho API
 */
export function LogPaymentImportInputDTO(csvData) {
  if (!Array.isArray(csvData)) {
    return [];
  }

  return csvData.map((row, index) => {
    if (!row || typeof row !== 'object') {
      return null;
    }

    // Helper function to ensure string values
    const ensureString = (value) => {
      if (value === null || value === undefined) return "";
      return value.toString().trim();
    };

    // Helper function to parse amount (remove currency symbols and convert to number)
    const parseAmount = (value) => {
      if (!value) return 0;
      // Remove all non-digit characters except dots and commas
      let cleanValue = value.toString().replace(/[^\d.,]/g, "");
      // Handle Vietnamese number format: 1.000.000 -> 1000000
      if (cleanValue.includes('.') && cleanValue.includes(',')) {
        // Format: 1.000.000,50 -> 1000000.50
        cleanValue = cleanValue.replace(/\./g, '').replace(',', '.');
      } else if (cleanValue.includes('.') && !cleanValue.includes(',')) {
        // Check if it's Vietnamese format (multiple dots) or decimal
        const parts = cleanValue.split('.');
        if (parts.length > 2) {
          // Vietnamese format: 1.000.000 -> 1000000
          cleanValue = cleanValue.replace(/\./g, '');
        }
        // Otherwise keep as decimal: 1000.50 -> 1000.50
      }
      return parseFloat(cleanValue) || 0;
    };

    // Helper function to parse integer values
    const parseIntValue = (value) => {
      if (!value || value === "") return null;
      const parsed = parseInt(value.toString());
      return isNaN(parsed) ? null : parsed;
    };

    // Helper: normalize keys defensively (in case upstream chưa convert header chuẩn)
    const normalizeKey = (k) => {
      return k
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // strip accents
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    };

    const safeGet = (obj, keys) => {
      for (const k of keys) {
        if (obj[k] !== undefined) return obj[k];
      }
      // Try normalized lookup
      const map = {};
      Object.keys(obj).forEach((ok) => {
        map[normalizeKey(ok)] = obj[ok];
      });
      for (const k of keys) {
        const nk = normalizeKey(k);
        if (map[nk] !== undefined) return map[nk];
      }
      return '';
    };

    const normalized = {
      rowIndex: index + 1,
      customer_phone: ensureString(safeGet(row, ['Số điện thoại','so_dien_thoai'])),
      contract_id: parseIntValue(safeGet(row, ['Mã hợp đồng','ma_hop_dong'])),
      amount: parseAmount(safeGet(row, ['Số tiền nạp','so_tien_nap'])),
      // Lưu raw text nếu người dùng dùng text, nếu là số thì convert sang label để preview
      payment_method: (() => {
        const raw = safeGet(row, ['Hình thức thanh toán','hinh_thuc_thanh_toan']);
        if (raw === null || raw === undefined || raw === '') return null;
        const rawStr = raw.toString().trim();
        // Nếu là số: map → label
        if (/^\d+$/.test(rawStr)) {
          const code = parseInt(rawStr, 10);
          return PaymentMethodLabel[code] || rawStr;
        }
        // Nếu là text: chuẩn hóa chữ cái đầu và trả nguyên văn
        return rawStr;
      })(),
      recipient_account: parseIntValue(safeGet(row, ['Tài khoản nhận tiền','tai_khoan_nhan_tien'])),
      card_purpose: parseIntValue(safeGet(row, ['Mục đích quẹt thẻ','muc_dich_quet_the'])),
      fee_payment_method: parseIntValue(safeGet(row, ['Hình thức thanh toán phí','hinh_thuc_thanh_toan_phi'])),
    };

    return normalized;
  }).filter(row => row !== null);
}

/**
 * Output DTO - Chuẩn hóa response từ API import
 * @param {Object} apiResponse - Response từ API
 * @returns {Object} Dữ liệu đã chuẩn hóa
 */
export function LogPaymentImportOutputDTO(apiResponse) {
  return {
    success: apiResponse.success || false,
    message: apiResponse.message || '',
    total: apiResponse.total || 0,
    successCount: apiResponse.success_count || 0,
    errorCount: apiResponse.error_count || 0,
    errors: apiResponse.errors || [],
    results: apiResponse.results || []
  };
}

/**
 * Validation Error DTO - Chuẩn hóa lỗi validation
 * @param {Object} error - Lỗi từ API hoặc validation
 * @returns {Object} Lỗi đã chuẩn hóa
 */
export function LogPaymentValidationErrorDTO(error) {
  return {
    row: error.row || error.rowIndex || 0,
    field: error.field || 'general',
    message: error.message || '',
    code: error.code || ''
  };
}

/**
 * Input DTO dành riêng cho payload gửi API
 * - Hình thức thanh toán gửi lên dạng text theo yêu cầu backend
 */
export function LogPaymentApiPayloadDTO(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(r => {
    const paymentMethodText = (() => {
      if (r.payment_method === null || r.payment_method === undefined) return null;
      const val = r.payment_method.toString();
      // Nếu người dùng chọn label, giữ nguyên
      if (isNaN(Number(val))) return val;
      // Nếu là mã số, map sang label
      const code = parseInt(val, 10);
      return PaymentMethodLabel[code] || val;
    })();

    return {
      rowIndex: r.rowIndex,
      customer_phone: r.customer_phone,
      contract_id: r.contract_id,
      amount: r.amount,
      payment_method: paymentMethodText,
      recipient_account: r.recipient_account,
      card_purpose: r.card_purpose,
      fee_payment_method: r.fee_payment_method,
    };
  });
}
